import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Shield } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
  allowedPermissions?: string[];
  requireAll?: boolean; // If true, user must have ALL permissions. If false, ANY permission is enough
  redirectTo?: string;
  fallback?: ReactNode;
}

/**
 * ProtectedRoute Component
 * Restricts access to routes based on user roles and permissions
 * 
 * Usage:
 * <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
 *   <AdminDashboard />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  allowedRoles,
  allowedPermissions,
  requireAll = false,
  redirectTo = '/unauthorized',
  fallback,
}: ProtectedRouteProps) {
  const { user, isAuthenticated, hasRole, hasPermission, hasAnyRole } = useAuth();


  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check role access
  if (allowedRoles && allowedRoles.length > 0) {
    const hasRequiredRole = allowedRoles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return fallback || <AccessDenied />;
    }
  }

  // Check permission access
  if (allowedPermissions && allowedPermissions.length > 0) {
    if (requireAll) {
      // User must have ALL permissions
      const hasAllPermissions = allowedPermissions.every(permission => hasPermission(permission));
      if (!hasAllPermissions) {
        return fallback || <AccessDenied />;
      }
    } else {
      // User must have ANY permission
      const hasAnyPermission = allowedPermissions.some(permission => hasPermission(permission));
      if (!hasAnyPermission) {
        return fallback || <AccessDenied />;
      }
    }
  }

  // All checks passed, render children
  return <>{children}</>;
}

/**
 * Default Access Denied Component
 */
function AccessDenied() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-red-500" />
          </div>
          <CardTitle className="text-2xl font-bold">Access Denied</CardTitle>
          <CardDescription className="text-base mt-2">
            You do not have permission to access this page. Please contact your administrator if you believe this is an error.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

export default ProtectedRoute;
